/// <amd-module name="Wit/DirectoryView/DirectoryDropdownProvider" />
import * as React from "react";
import { IObservableArray, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { css } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { IIconProps } from "VSSUI/Icon";
import { renderListCell } from "VSSUI/List";
import { IListBoxGroup, IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { GroupIds, IDirectoryService } from "Wit/DirectoryService/IDirectoryService";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import { DirectoryFavoriteButton } from "Wit/DirectoryView/Components/DirectoryFavoriteButton";
import { TeamProfilePanelIconButton } from "TFS/TeamProfilePanelIconButton/TeamProfilePanelIconButton";
import * as Resources from "Wit/DirectoryView/Resources";

interface IDirectoryDropdownProviderArgs {
    directoryService: IDirectoryService;
    artifactTypeName: string;
    iconProps: IIconProps;
    directoryHref?: string;
    favoriteType?: string;
    hideActions?: boolean;
    hideFavorites?: boolean;
    hideGroups?: boolean;
    newSprintsViewEnabled?: boolean; // Remove this with FF WebPlatform.WorkItemTracking.NewSprintsBacklogsView
}

export class DirectoryDropdownProvider extends GroupedItemProvider<IDirectoryDropdownItem> {
    public artifactTypeName: string;
    public favoriteType?: string;
    public iconProps: IIconProps;

    private loading = new ObservableValue<boolean>(true);
    private dataLoadPromise: Promise<IDirectoryDropdownItem[]>;
    private actions: IObservableArray<IButtonProps>;
    private showActions: boolean;
    private showFavorites: boolean;
    private showGroups: boolean;
    private directoryService: IDirectoryService;
    private directoryHref?: string;
    private newSprintsViewEnabled?: boolean;

    constructor(args: IDirectoryDropdownProviderArgs) {
        super([], [], true);
        this.showActions = !args.hideActions;
        this.showFavorites = !args.hideFavorites;
        this.showGroups = !args.hideGroups;
        this.directoryService = args.directoryService;
        this.artifactTypeName = args.artifactTypeName;
        this.favoriteType = args.favoriteType;
        this.iconProps = args.iconProps;
        this.directoryHref = args.directoryHref;
        this.newSprintsViewEnabled = args.newSprintsViewEnabled || false;

        const loadingItem: IListBoxItem<IDirectoryDropdownItem> = {
            id: "loading",
            type: ListBoxItemType.Loading,
            render: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) => {
                return <LoadingCell key={rowIndex} columnIndex={columnIndex} tableColumn={tableColumn} tableItem={tableItem} />;
            }
        };

        this.push(loadingItem);
    }

    public isLoading(): IReadonlyObservableValue<boolean> {
        return this.loading;
    }

    public async loadData() {
        if (!this.dataLoadPromise) {
            this.dataLoadPromise = this.directoryService.getDirectoryDropdownData();
            const data = await this.dataLoadPromise;
            this.removeAll();
            if (this.showGroups) {
                const groups: IListBoxGroup<IDirectoryDropdownItem>[] = [
                    {
                        id: GroupIds.Favorites,
                        name: format(Resources.FavoriteTeamsLabel, this.artifactTypeName)
                    },
                    {
                        id: GroupIds.Team,
                        name: format(Resources.MyTeamItemsLabel, this.artifactTypeName)
                    },
                    {
                        id: GroupIds.All,
                        name: format(Resources.AllTeamsLabel, this.artifactTypeName)
                    }
                ];
                this.pushGroups(...groups);
            }

            const mapped = data.map(this.getListBoxItemFromDirectoryDropdownItem);
            this.push(...mapped);

            this.loading.value = false;
        }
    }

    public getActions(): IReadonlyObservableArray<IButtonProps> {
        if (!this.actions) {
            this.actions = new ObservableArray<IButtonProps>();

            if (this.showActions && this.directoryHref) {
                this.actions.push({
                    text: format(Resources.ViewDirectoryLabel, this.artifactTypeName),
                    href: this.directoryHref,
                    className: "directory-dropdown-action"
                });
            }
        }

        return this.actions;
    }

    private getListBoxItemFromDirectoryDropdownItem = (item: IDirectoryDropdownItem): IListBoxItem<IDirectoryDropdownItem> => {
        return {
            id: item.id,
            data: item,
            groupId: this.showGroups ? item.group : undefined,
            text: item.text,
            iconProps: { ...this.iconProps },
            render: this.renderDirectoryDropdownItem
        };
    };

    private renderDirectoryDropdownItem = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem<IDirectoryDropdownItem>>,
        tableItem: IListBoxItem<IDirectoryDropdownItem>
    ): JSX.Element => {
        const directory = tableItem.data?.directory;

        return (
            <SimpleTableCell
                className={css(tableColumn.className, tableItem.className, tableItem.type === ListBoxItemType.Header && "bolt-list-box-header")}
                columnIndex={columnIndex}
                key={columnIndex}
                tableColumn={tableColumn}
            >
                {renderListCell(
                    {
                        ...tableItem,
                        textClassName: "flex-grow directory-dropdown-link"
                    },
                    true
                )}
                {this.showFavorites && directory?.id && this.favoriteType && (
                    <>
                        <DirectoryFavoriteButton
                            artifactType={this.favoriteType}
                            item={directory}
                            className="flex-end bolt-table-cell-content-reveal directory-favorite-cell"
                        />
                        { this.newSprintsViewEnabled && (<TeamProfilePanelIconButton teamId={directory.id} teamName={directory.name} />)}
                    </>
                )}
            </SimpleTableCell>
        );
    };
}
